/**
 * Product page extras: trade price breaks and back-in-stock capture.
 *
 * Both are rendered server-side for the variant the page loaded on, so without
 * this file the page is still correct - it just stops following the pills.
 *
 * This is a separate file from alpha-product.js on purpose. That element owns
 * the gallery, the pickers and the buy form; it exposes no variant-change event
 * and adding one is not this feature's change to make. Instead the selection is
 * re-derived from the pills alpha-product.js has ALREADY updated: the listener
 * below is bound to the <alpha-product> ancestor, so a click on a pill reaches
 * it only after the pill's own handler has run. One source of truth, one JSON
 * blob, no second copy of the variant data.
 *
 * It is a custom element rather than a one-shot script for a reason that cost a
 * debugging round: the theme editor and the dev server's hot reload both REPLACE
 * a section's markup in place. A script that bound its listeners once at load is
 * left holding a detached node - the pills carry on working, because they belong
 * to a custom element that re-runs connectedCallback on the new markup, while
 * the price break table silently stops following them.
 */

if (!customElements.get('alpha-pdp-extras')) {
  customElements.define(
    'alpha-pdp-extras',
    class AlphaPdpExtras extends HTMLElement {
      connectedCallback() {
        // The controls are spread through the buy box - the tier table sits
        // with the quantity stepper, the capture form under Add to cart - so
        // the shared ancestor is the element to work from.
        this.root = this.closest('alpha-product');
        if (!this.root) return;

        this.variants = this.readVariants();
        this.swatches = Array.from(this.root.querySelectorAll('[data-alpha-swatch]'));

        this.breaks = this.root.querySelector('[data-alpha-breaks]');
        this.notify = this.root.querySelector('[data-alpha-notify]');

        if (!this.breaks && !this.notify) return;

        this.pruneStraySuccess();

        // Nothing can change the selection without a full page load, so the
        // server-rendered state is final. Binding anyway would risk replacing a
        // correct render with a guess.
        if (!this.swatches.length || !this.variants.length) return;

        this.onClick = (event) => {
          if (!event.target.closest('[data-alpha-swatch]')) return;
          this.sync();
        };

        this.root.addEventListener('click', this.onClick);
      }

      disconnectedCallback() {
        if (this.root && this.onClick) this.root.removeEventListener('click', this.onClick);
      }

      /**
       * Shopify raises `form.posted_successfully?` on every customer form on
       * the page, so subscribing in the footer would also leave this product
       * claiming a restock alert nobody asked for. Shopify redirects to the
       * posting form's id as the document fragment, which Liquid cannot read
       * but this can: a fragment naming some other form means the confirmation
       * here is not ours to show.
       */
      pruneStraySuccess() {
        if (!this.notify) return;

        const done = this.notify.querySelector('[data-alpha-notify-done]');
        if (!done) return;

        const hash = window.location.hash.replace('#', '');
        // No fragment at all is not evidence either way - leave the
        // server-rendered answer alone rather than hiding a real confirmation.
        if (!hash) return;

        const form = this.notify.querySelector('form');
        if (form && form.id === hash) return;

        this.notify.hidden = true;
      }

      readVariants() {
        const node = this.root.querySelector('script[data-alpha-variants]');
        if (!node) return [];

        try {
          const parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          // The buy box is already rendered correctly for the current variant;
          // leaving it alone beats clearing it.
          return [];
        }
      }

      /** Read the selection back off the pills, which carry aria-current. */
      readSelection() {
        const selection = [];

        this.swatches.forEach((swatch) => {
          const position = parseInt(swatch.dataset.optionPosition, 10);
          if (!position) return;
          if (swatch.getAttribute('aria-current') === 'true') {
            selection[position - 1] = swatch.dataset.optionValue;
          }
        });

        return selection;
      }

      /**
       * The variant matching every selected option, or null for a combination
       * the merchant never created - which is a different fact from "sold out"
       * and must not be offered a back-in-stock alert.
       */
      currentVariant() {
        const selection = this.readSelection();
        if (!selection.length) return null;

        return (
          this.variants.find((variant) =>
            selection.every((value, index) => variant.options[index] === value)
          ) || null
        );
      }

      sync() {
        const variant = this.currentVariant();
        this.syncBreaks(variant);
        this.syncNotify(variant);
      }

      /**
       * Price breaks are B2B catalogue data, so most variants have none. Hiding
       * the box entirely - rather than leaving an empty framed heading - is the
       * whole reason each variant's rows are their own list.
       */
      syncBreaks(variant) {
        if (!this.breaks) return;

        const id = variant ? String(variant.id) : '';
        let matched = false;

        this.breaks.querySelectorAll('[data-alpha-breaks-variant]').forEach((list) => {
          const isCurrent = list.dataset.alphaBreaksVariant === id;
          list.hidden = !isCurrent;
          if (isCurrent) matched = true;
        });

        this.breaks.hidden = !matched;
      }

      /**
       * Back-in-stock capture. Shown only for a real variant that is really
       * unavailable - `variant.available` is Shopify's own answer, so nothing
       * here invents a stock position.
       */
      syncNotify(variant) {
        if (!this.notify) return;

        const show = Boolean(variant) && variant.available === false;
        this.notify.hidden = !show;
        if (!show) return;

        // The tag list is composed by Liquid, once per variant, and simply copied
        // across here. Rebuilding it in JavaScript is how a merchant ends up with
        // two different tag formats in their customer list - and the tag is what
        // the notification automation triggers on, so a stray format means an
        // address that never gets emailed.
        const tags = this.notify.querySelector('[data-alpha-notify-tags]');
        if (tags && typeof variant.notify === 'string') tags.value = variant.notify;

        const label = this.notify.querySelector('[data-alpha-notify-variant]');
        if (label) label.textContent = variant.label || '';
      }
    }
  );
}
